import { ArrowLeft, ChevronDown, CircleAlert, Clock3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { wave, waveQueries } from '@round/api';
import { formatDeadline, getInviteLifecycleStatus, getTimeLeft } from '@/utility/campaignDisplay';
import { showNotification } from '@/utility/showNotification';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import { BriefText } from '@/ui/BriefSection/BriefSection';
import StatusPill from '@/creator/components/StatusPill';
import BudgetUsedMeter from '@/creator/components/BudgetUsedMeter';
import CampaignArtwork from './CampaignArtwork';
import CampaignOverview from './CampaignOverview';
import { getRespondToInviteBody } from '@/utility/inviteContract';
import ContractList from '@/creator/components/ContractCard/ContractList';
import { getOfferPosts } from '@/utility/offerPosts';
import type { ContractResponse } from '@/creator/components/ContractCard/ContractCard';

const MINUTE_MS = 60 * 1000;
const DECLINE_REASONS = ['Deadline', 'Offer', 'Campaign fit', 'Other'];

const CampaignDetail = () => {
    const { campaignId = '' } = useParams<{ campaignId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAuthenticated } = useWaveAuth();

    const query = useQuery(waveQueries.invite.options.detail(campaignId, isAuthenticated));
    const contractsQuery = useQuery(waveQueries.contract.options.forInvite(campaignId, isAuthenticated));
    // The route carries the invite uuid, so the campaign pk that scopes this
    // creator's posts is only known once the invite detail loads.
    const postsQuery = useQuery(waveQueries.creatorPost.options.forCampaign(query.data?.campaign_id));

    const [briefExpanded, setBriefExpanded] = useState(false);
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), MINUTE_MS);
        return () => window.clearInterval(intervalId);
    }, []);

    const invite = query.data ?? null;
    const contracts = contractsQuery.data ?? [];

    const goBack = (): void => {
        navigate('/campaigns');
    };

    const refetchAll = async (): Promise<void> => {
        await Promise.all([
            query.refetch(),
            contractsQuery.refetch(),
            postsQuery.refetch(),
            queryClient.invalidateQueries({ queryKey: waveQueries.invite.keys.mine() }),
            queryClient.invalidateQueries({ queryKey: waveQueries.contract.keys.all() }),
        ]);
    };

    const handleRespond = async (contract: wave.CreatorContract, answer: ContractResponse): Promise<boolean> => {
        try {
            const result = await wave.respondToInvite(
                campaignId,
                getRespondToInviteBody(contract, answer.response, answer.reason ?? '', answer.creatorAccounts)
            );
            if (result.status === 200) {
                await refetchAll();
                setNowMs(Date.now());
                // The backend answers an offer it can no longer change with its unchanged state.
                if (result.data.response !== answer.response) {
                    showNotification('This offer couldn’t be changed. Refresh the page and try again.', 'error');
                    return false;
                }
                showNotification(
                    answer.response === 'accepted'
                        ? "You're in. We've let the brand know."
                        : "Thanks for letting us know you're not taking this one.",
                    'success'
                );
                return true;
            }
            if (result.status === 409) {
                showNotification(result.data.reason, 'error');
                await refetchAll();
                return false;
            }
            showNotification('We couldn’t save your response. Please try again.', 'error');
            return false;
        } catch {
            showNotification('We couldn’t save your response. Please try again.', 'error');
            return false;
        }
    };

    const handleSubmitPost = async (contract: wave.CreatorContract, postUrl: string): Promise<string | null> => {
        try {
            const result = await wave.submitPost(campaignId, { post_url: postUrl, contract_id: contract.id });
            if (result.status === 201 || result.status === 200) {
                await refetchAll();
                showNotification(
                    result.status === 201
                        ? 'Post submitted. It’s in for review.'
                        : 'That post is already in for this offer.',
                    result.status === 201 ? 'success' : 'info'
                );
                return null;
            }
            if (result.status === 409) {
                await refetchAll();
                return result.data.reason || 'This offer can’t take another post right now.';
            }
            if (result.status === 400) {
                const error = result.data.post_url ?? result.data.contract_id;
                return (
                    (Array.isArray(error) ? error[0] : error) ?? 'We couldn’t accept that link. Check it and try again.'
                );
            }
            return 'We couldn’t submit your post. Please try again.';
        } catch {
            return 'We couldn’t submit your post. Please try again.';
        }
    };

    if (query.isLoading || contractsQuery.isLoading) {
        return (
            <div className="page detail-page">
                <button className="back-button" type="button" onClick={goBack}>
                    <ArrowLeft size={16} /> <span>Back</span>
                </button>
                <section className="action-section">
                    <p>Loading campaign…</p>
                </section>
            </div>
        );
    }

    if (query.isError || !invite) {
        return (
            <div className="page detail-page">
                <button className="back-button" type="button" onClick={goBack}>
                    <ArrowLeft size={16} /> <span>Back</span>
                </button>
                <section className="action-section">
                    <h2>Campaign not found</h2>
                    <p>This campaign is no longer available.</p>
                </section>
            </div>
        );
    }

    // `/campaigns/:inviteUuid` is the authenticated owner surface. A signed-in
    // non-owner who pastes another creator's UUID is sent to the campaign's generic
    // public link; the invite is never reassigned or rendered to them here.
    if (!invite.viewer_is_owner) {
        return <Navigate to={`/c/${invite.public_token}`} replace />;
    }

    const submissionAvailability = invite.submission_availability;
    const status = getInviteLifecycleStatus(invite);
    const closesAt = formatDeadline(invite.deadline);
    const timelineLabel =
        status === 'declined'
            ? 'Invitation declined'
            : status === 'closed'
              ? 'Campaign closed'
              : `${getTimeLeft(invite.deadline)}${closesAt ? ` · closes ${closesAt}` : ''}`;

    return (
        <div className="page detail-page">
            <button className="back-button" type="button" onClick={goBack}>
                <ArrowLeft size={16} /> <span>Back</span>
            </button>
            <section className="campaign-header">
                <CampaignArtwork
                    accent="sky"
                    size="detail"
                    src={invite.artwork_url}
                    alt={`${invite.release_name} artwork`}
                />
                <div className="campaign-header__copy">
                    <div className="campaign-header__intro">
                        <StatusPill status={status} />
                        <h1>{invite.release_name}</h1>
                        <span className="deadline">
                            <Clock3 size={14} /> <span>{timelineLabel}</span>
                        </span>
                        <BudgetUsedMeter percentage={invite.budget_used_percentage} />
                    </div>
                </div>
            </section>

            <section className="campaign-detail-layout">
                <aside className="campaign-detail-sidebar">
                    <CampaignOverview invite={invite} contracts={contracts} />
                </aside>
                <div className="campaign-detail-main">
                    <section className={`public-section public-brief ${briefExpanded ? 'is-expanded' : ''}`}>
                        <div className="public-section__heading">
                            <h2>The brief</h2>
                            <button
                                type="button"
                                onClick={() => setBriefExpanded((current) => !current)}
                                aria-expanded={briefExpanded}
                            >
                                <span>{briefExpanded ? 'Collapse brief' : 'Expand brief'}</span>
                                <ChevronDown size={14} />
                            </button>
                        </div>
                        <div className="public-brief__copy">
                            <BriefText brief={invite.brief} />
                        </div>
                    </section>

                    {contractsQuery.isError ? (
                        <section className="submission-limit-notice">
                            <CircleAlert size={16} />
                            <div>
                                <h2>We couldn’t load your offers</h2>
                                <p>Refresh the page to try again.</p>
                            </div>
                        </section>
                    ) : (
                        <ContractList
                            contracts={contracts}
                            invite={invite}
                            nowMs={nowMs}
                            posts={getOfferPosts({ contracts, invite, creatorPosts: postsQuery.data })}
                            onRespond={submissionAvailability === 'ended' ? undefined : handleRespond}
                            onSubmitPost={submissionAvailability === 'ended' ? undefined : handleSubmitPost}
                            declineReasons={DECLINE_REASONS}
                        />
                    )}

                    {submissionAvailability === 'ended' ? (
                        <section className="submission-limit-notice">
                            <Clock3 size={16} />
                            <div>
                                <h2>Submissions are closed</h2>
                                <p>The window for this campaign has ended.</p>
                            </div>
                        </section>
                    ) : null}
                </div>
            </section>
        </div>
    );
};

export default CampaignDetail;
